<template>
  <div class="chart">
    <div class="svg-wrapper">
      <svg ref="svg" overflow="visible" :width="outerWidth" :height="outerHeight">
        <template v-if="width && height">
          <g ref="xAxisGroup"
            :transform="`translate(0, ${lowestY})`">
          </g>

          <g ref="yAxisGroup"
            :transform="`translate(${margin.left}, 0)`">
            <template v-if="config.yAxis && config.yAxis.label">
              <text
                v-for="label in yAxisLabels"
                v-html=label.text
                :key="label.id"
                :x="label.x"
                :y="label.y"
                :transform="`rotate(${label.rotate})`"
                :fill="label.textColor"
                :text-anchor="label.textAnchor"
                :style="`font: ${label.fontWeight} ${label.fontSize}px ${label.fontFamily}`"
              ></text>
            </template>
          </g>

          <line
            v-if="data.length && selectedColumn"
            stroke-dasharray="2,2"
            stroke="#000"
            :x1="selectedColumn.x1"
            :x2="selectedColumn.x2"
            :y1="selectedColumn.y1"
            :y2="selectedColumn.y2"
            />

          <template v-for="(shadedRegion, i) in shadedRegions">
            <rect
              :key="`shaded-region-rect-${i}`"
              :x="shadedRegion.rect.x"
              :y="shadedRegion.rect.y"
              :width="shadedRegion.rect.width"
              :height="shadedRegion.rect.height"
              :fill="shadedRegion.rect.backgroundColor"
              />
            <text
              text-anchor="middle"
              :key="`shaded-region-label-${i}`"
              :x="shadedRegion.label.x"
              :y="shadedRegion.label.y"
              :style="`font: ${shadedRegion.label.font}`"
            >{{ shadedRegion.label.text }}</text>
          </template>

          <!-- TREND LINE -->
          <g
            v-for="trendLine in trendLines"
            :key="trendLine.id">
            <line
              :stroke-dasharray="trendLine.dashArray"
              :x1="trendLine.x1"
              :x2="trendLine.x2"
              :y1="trendLine.y1"
              :y2="trendLine.y2"
              :stroke="trendLine.color"
            />

            <template v-if="trendLine.dotRadius != null">
              <circle
                :cx="trendLine.x1"
                :cy="trendLine.y1"
                :r="trendLine.dotRadius"
                :fill="trendLine.color" />
              <circle
                :cx="trendLine.x2"
                :cy="trendLine.y2"
                :r="trendLine.dotRadius"
                :fill="trendLine.color" />
            </template>
            <template v-else>
              <text
                :x="trendLine.labelX"
                :y="trendLine.labelY"
                :fill="trendLine.textColor"
                :text-anchor="trendLine.textAnchor"
                :style="`font: ${trendLine.fontSize}px ${trendLine.fontFamily}`"
              >{{ trendLine.text }}</text>
            </template>
          </g>

          <!-- LINES -->
          <g class="lines"
            v-for="l in lines"
            :key="l.id"
            :class="`label-${l.set}`">
            <path
              :d="l.d"
              :fill="l.fill"
              :stroke="l.color"
              :stroke-dasharray="l.lineStyle"
              :stroke-width="strokeWidth" />
          </g>

          <!-- EXTREMES -->
          <template v-if="!config.hideExtremes">
            <g
              v-for="e in extremes"
              :key="e.id"
              :class="`label-${e.set}`"
              :transform="`translate(${e.x}, ${e.y})`">
              <line
                :x1="e.top.x1"
                :x2="e.top.x2"
                :y1="e.top.y1"
                :y2="e.top.y2"
                :stroke="e.top.color"
                :stroke-width="e.strokeWidth" />
              <line
                :x1="e.vertical.x1"
                :x2="e.vertical.x2"
                :y1="e.vertical.y1"
                :y2="e.vertical.y2"
                :stroke="e.vertical.color"
                :stroke-width="e.strokeWidth" />
              <line
                :x1="e.bottom.x1"
                :x2="e.bottom.x2"
                :y1="e.bottom.y1"
                :y2="e.bottom.y2"
                :stroke="e.bottom.color"
                :stroke-width="e.strokeWidth" />
            </g>
          </template>

          <!-- DOTS -->
          <g class="dots">
            <template v-for="d in dots">
            <circle
              stroke-width="2"
              :key="d.id"
              :cx="d.x"
              :cy="d.y"
              :r="d.dotRadius"
              :stroke="d.color"
              :fill="d.fill"
              :class="`label-${d.set}`" />

            <circle
              opacity="0"
              style="cursor: pointer"
              @click="selectedPoint = { x: d.xValue, y: d.yValue }"
              :key="d.id"
              :cx="d.x"
              :cy="d.y"
              :r="d.dotRadius + 3"
              :class="`label-${d.set}`" />
            </template>
          </g>

          <!-- LABELS -->
          <template v-for="l in labels">
            <text
              v-if="l"
              v-html="l.text"
              fill="#000"
              text-anchor="middle"
              :key="l.id"
              :x="l.x"
              :y="l.y"
              :style="`font: ${l.font}`"
            />
          </template>
        </template>
      </svg>
    </div>

    <!-- X-AXIS LABEL -->
    <div
      v-if="config.xAxis && config.xAxis.label && xAxisLabels && xAxisLabels.length"
      class="x-label-container"
      :style="`margin-left: ${margin.left}px; margin-right: ${margin.right}px;`">
      <div
        class="x-label"
        v-for="label in xAxisLabels"
        v-html="label.text"
        :key="label.id"
        :style="`font-weight: ${label.fontWeight}; font-size: ${label.fontSize}px; font-family: ${label.fontFamily}; color: ${label.textColor}`"
      />
    </div>

    <div
      v-if="config.nValueTable"
      class="n-values-table"
      :style="`height: ${getNValueTableHeight()}px`"
    >
      <NValueTable
        :config="config"
        :data="nValuesData"
        :type="config.nValueTable.type"
        :rows="config.nValueTable.rows"
        :firstColumnWidth="margin.left"
        :valuesColumnWidth="xBandwidth"
        :style="`margin-right: ${margin.right}px`"
      />
    </div>

    <!-- Tooltip -->
    <div
      ref="tooltip"
      class="linechart-tooltip"
      v-if="data.length && selectedPoint"
      @click="closeTooltip()"
      :style="`top: ${tooltipTop}px; left: ${tooltipLeft}px; visibility: ${tooltipVisibility}; transition: top linear ${tooltipTransition}, left linear ${tooltipTransition};`">
      <table>
        <thead v-if="tooltipHeaders && tooltipHeaders.length">
          <tr>
            <td
              v-for="header of tooltipHeaders"
              :key="header"
              class="tooltip-headers"
            >{{ header }}</td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item of tooltipData"
            :key="item.id"
            class="tooltip-item"
          >
            <td>
              <span class="tooltip-set">
                <span :style="`border-color: ${item.color}; background: ${item.background}`"></span>
                <span :style="`font-weight: ${item.fontWeight}`">{{ item.label }}:</span>
              </span>
            </td>
            <td
              v-html="item.yValue"
              :style="`font-weight: ${item.fontWeight}`"
            ></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, toRefs } from "@vue/composition-api";

import NValueTable from "./NValueTable";
import useLineChartDataSet from "../composables/LineChart/useLineChartDataSet";
import useLineChartAxes from "../composables/LineChart/useLineChartAxes";
import useLineChartLines from "../composables/LineChart/useLineChartLines";
import useLineChartExtremes from "../composables/LineChart/useLineChartExtremes";
import useLineChartLabels from "../composables/LineChart/useLineChartLabels";
import useLineChartDots from "../composables/LineChart/useLineChartDots";
import useLineChartTooltip from "../composables/LineChart/useLineChartTooltip";
import useLineChartShadedArea from "../composables/LineChart/useLineChartShadedArea";
import useChartScales from "../composables/useChartScales";
import useDynamicSizing from "../composables/useDynamicSizing";
import useChartXAxisLabel from "../composables/useChartXAxisLabel";
import useChartYAxisLabel from "../composables/useChartYAxisLabel";
import useChartTrendLines from "../composables/useChartTrendLines";
import useIPadProDetection from "../composables/useIPadProDetection";

export default {
  name: "LineChart",
  components: {
    NValueTable
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    margin: {
      type: Object,
      required: true,
    },
    outerWidth: {
      type: Number,
    },
    outerHeight: {
      type: Number,
    },
  },

  computed: {
    strokeWidth() {
      return this.config?.strokeWidth ?? 1.5;
    },
  },

  methods: {
    getNValueTableHeight() {
      const { type, rows } = this.config?.nValueTable ?? {};

      let length;
      switch (type) {
        case 'regular':
          length = rows ?? 2;
          break;

        case 'show-all-series':
          length = rows ?? this.nValuesData.length;
          break;

        default:
          length = 0;
          break;
      }

      const totalLineHeight = length * 30;
      const totalLineSpace = (length - 1) * 2;
      const paddingTop = 10;

      return totalLineHeight + totalLineSpace + paddingTop;
    },
  },

  setup(props) {
    const { data, margin, config } = toRefs(props);

    // Vue matches the DOM refs based on the returned variable names
    const xAxisGroup = ref(null);
    const yAxisGroup = ref(null);
    const svg = ref(null);
    const tooltip = ref(null);

    const { isIPadPro } = useIPadProDetection();
    const { width, height, availableWidth, availableHeight } = useDynamicSizing(svg, margin);
    const { x, y, xBandwidth } = useChartScales(margin, config, availableWidth, availableHeight);
    const { lowestY } = useLineChartAxes(x, y, config, xAxisGroup, yAxisGroup, width, height, isIPadPro);

    const { xAxisLabels } = useChartXAxisLabel(config, availableWidth, margin, isIPadPro);
    const { yAxisLabels } = useChartYAxisLabel(config, availableHeight, margin, isIPadPro);

    const { lineChartDataSet, nValuesData } = useLineChartDataSet(config, data);
    const { shadedRegions } = useLineChartShadedArea(config, availableWidth, availableHeight, x, y, xBandwidth, isIPadPro);
    const { lines } = useLineChartLines(lineChartDataSet, availableWidth, availableHeight, x, y, xBandwidth);
    const { extremes } = useLineChartExtremes(lineChartDataSet, availableWidth, availableHeight, x, y, xBandwidth);
    const { labels } = useLineChartLabels(lineChartDataSet, availableWidth, availableHeight, x, y, xBandwidth, isIPadPro);
    const { dots } = useLineChartDots(lineChartDataSet, availableWidth, availableHeight, x, y, xBandwidth);
    const { trendLines } = useChartTrendLines(config, availableWidth, availableHeight, margin, x, y, xBandwidth, isIPadPro);

    const {
      selectedColumn,
      selectedPoint,
      tooltipData,
      tooltipTop,
      tooltipLeft,
      tooltipVisibility,
      tooltipTransition,
      tooltipHeaders,
      closeTooltip,
    } = useLineChartTooltip(x, y, xBandwidth, config, data, tooltip, margin, availableWidth, availableHeight);

    return {
      svg,
      tooltip,
      width,
      height,
      lowestY,
      availableWidth,
      availableHeight,
      lineChartDataSet,
      nValuesData,
      xAxisGroup,
      yAxisGroup,
      shadedRegions,
      lines,
      xBandwidth,
      extremes,
      labels,
      dots,
      trendLines,
      xAxisLabels,
      yAxisLabels,
      tooltipData,
      selectedColumn,
      selectedPoint,
      tooltipTop,
      tooltipLeft,
      tooltipVisibility,
      tooltipTransition,
      tooltipHeaders,
      closeTooltip,
    };
  },
};
</script>

<style lang="scss" scoped>
  .chart {
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .n-values-table, .x-label-container {
    flex: 0 1 auto;
  }

  .x-label {
    text-align: center;
    line-height: 1;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .svg-wrapper {
    flex: 1 1 100%;
  }

  .linechart-tooltip {
    position: absolute;
    border: 2px solid #2d6c87;
    background: #fff;
    border-radius: 15px;
    padding: 16px 20px;

    font-family: Roboto, sans-serif;
    font-size: rem(12);
    color: #646464;
    text-align: left;
    line-height: 2;

    @include iPadProStyles {
      font-size: rem(16);
    }
  }

  .tooltip-item {
    pointer-events: none;

    td:last-child {
      text-align: right;
    }
  }

  .tooltip-set {
    padding-right: 20px;

    :first-child {
      border-style: solid;
      border-width: 2px;
      display: inline-block;
      margin: -2px 10px 0 0;
      border-radius: 100%;
      width: 15px;
      height: 15px;
      vertical-align: middle;
    }
  }

  .tooltip-headers {
    font-family: Roboto, sans-serif;
    font-size: rem(12);
    font-weight: 500;
    color: #646464;

    @include iPadProStyles {
      font-size: rem(16);
    }

    &:first-child {
      padding-right: 15px;
    }
  }

  .n-values-table {
    padding-top: 10px;
  }
</style>
